import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaUserShield } from 'react-icons/fa';
import { LuAlertTriangle, LuMerge, LuSearch, LuX } from 'react-icons/lu';

import { Drawer, Button, LoadingSpinner } from 'components/common';
import { combineRoles, getCombineRolesPreview } from 'redux/authorization/roleSlice';

// Gộp role: chọn một role ĐÍCH, chọn các role NGUỒN, rồi hợp permission và
// chuyển toàn bộ user sang đích trước khi xoá nguồn.
//
// Dùng chung cho hai màn hình, khác nhau ở chỗ ai được làm đích:
//   - Role detail  → đích cố định là role đang xem (lockedTargetId);
//   - User detail  → đích chọn trong các role của user đó.
// Dưới ngưỡng này thì cuộn mắt nhanh hơn gõ, ô tìm kiếm chỉ thành nhiễu.
// User detail thường chỉ có dăm role; Role detail thì liệt kê cả hệ thống.
const SEARCH_THRESHOLD = 8;

const CombineRolesDrawer = ({ open, setOpen, roles, lockedTargetId, onCombined }) => {
  const dispatch = useDispatch();

  const [targetId, setTargetId]   = useState(lockedTargetId ?? null);
  const [sourceIds, setSourceIds] = useState([]);
  const [preview, setPreview]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [term, setTerm]           = useState('');

  useEffect(() => {
    if (!open) return;
    setTargetId(lockedTargetId ?? null);
    setSourceIds([]);
    setPreview(null);
    setTerm('');
  }, [open, lockedTargetId]);

  // Role đích không bao giờ được nằm trong danh sách nguồn — backend cũng từ
  // chối, nhưng để nó hiện ra rồi báo lỗi thì thà đừng hiện.
  const sourceOptions = useMemo(
    () => (roles || []).filter(role => role.id !== targetId),
    [roles, targetId]
  );

  const showSearch = sourceOptions.length > SEARCH_THRESHOLD;

  // Role đang được tick luôn hiển thị dù không khớp từ khoá — lọc mất nó đi thì
  // người dùng không còn thấy mình đã chọn những gì trước khi bấm Combine.
  const visibleSources = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return sourceOptions;

    return sourceOptions.filter(role =>
      sourceIds.includes(role.id) ||
      role.name?.toLowerCase().includes(needle) ||
      String(role.id).includes(needle)
    );
  }, [sourceOptions, sourceIds, term]);

  // Đổi đích thì bỏ nó ra khỏi danh sách nguồn đang chọn.
  useEffect(() => {
    setSourceIds(ids => ids.filter(id => id !== targetId));
  }, [targetId]);

  useEffect(() => {
    if (!open || !targetId || sourceIds.length === 0) {
      setPreview(null);
      return undefined;
    }
    let active = true;

    setLoading(true);
    dispatch(getCombineRolesPreview({ roleId: targetId, sourceRoleIds: sourceIds }))
      .unwrap()
      .then(res => { if (active) setPreview(res); })
      .catch(error => { if (active) toast.error(error?.message || 'Failed to preview the combine.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [open, targetId, sourceIds, dispatch]);

  const targetRole = (roles || []).find(role => role.id === targetId);

  const toggleSource = (id) => setSourceIds(ids => (
    ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
  ));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await dispatch(combineRoles({ roleId: targetId, sourceRoleIds: sourceIds })).unwrap();
      onCombined?.();
      setOpen(false);
    } catch (error) {
      toast.error(error?.message || 'Failed to combine roles.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = Boolean(targetId) && sourceIds.length > 0 && !submitting;

  return (
    <Drawer
      show={open}
      onHide={() => setOpen(false)}
      backdropClick={false}
      width="46%"
      minWidth="480px"
      title={
        <span className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-w-6 tw-h-6 tw-bg-blue-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
            <LuMerge size={13} className="tw-text-blue-600" />
          </span>
          Combine roles
        </span>
      }
      footer={
        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
          <p className="tw-text-xs tw-text-slate-400 tw-m-0">
            {sourceIds.length > 0 ? `${sourceIds.length} role${sourceIds.length > 1 ? 's' : ''} will be deleted` : 'No role selected'}
          </p>
          <div className="tw-flex tw-gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="button"
              size="sm"
              variant="primary"
              loading={submitting}
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              <LuMerge size={13} /> Combine
            </Button>
          </div>
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm tw-h-full">
        {/* Đích */}
        <div className="tw-flex-shrink-0">
          <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 tw-mb-1.5">
            Keep this role
          </p>
          {lockedTargetId ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50">
              <FaUserShield size={13} className="tw-text-blue-500 tw-flex-shrink-0" />
              <span className="tw-font-semibold tw-text-slate-800">{targetRole?.name || `Role #${lockedTargetId}`}</span>
            </div>
          ) : (
            <div className="tw-space-y-1 tw-overflow-y-auto tw-pr-1" style={{ maxHeight: '12rem' }}>
              {(roles || []).map(role => (
                <label
                  key={role.id}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded tw-border tw-border-slate-200 hover:tw-bg-slate-50 tw-cursor-pointer"
                >
                  <input
                    type="radio"
                    name="combine-target"
                    checked={targetId === role.id}
                    onChange={() => setTargetId(role.id)}
                    className="tw-cursor-pointer tw-flex-shrink-0"
                  />
                  <span className="tw-truncate tw-text-slate-700">{role.name}</span>
                  <span className="tw-ml-auto tw-flex-shrink-0 tw-text-[11px] tw-text-slate-400">ID#{role.id}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Nguồn — nở ra chiếm hết phần còn lại của drawer */}
        <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1.5 tw-flex-shrink-0">
            <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 tw-mb-0">
              Absorb and delete these roles
            </p>
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-[11px] tw-text-slate-400 tw-tabular-nums">
              {term.trim()
                ? `${visibleSources.length} of ${sourceOptions.length}`
                : `${sourceIds.length} of ${sourceOptions.length} selected`}
            </span>
            {sourceIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSourceIds([])}
                className="tw-flex-shrink-0 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-text-blue-600 hover:tw-underline tw-cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          {showSearch && (
            <div className="tw-relative tw-mb-2 tw-flex-shrink-0">
              <LuSearch size={14} className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" />
              <input
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search by role name or ID…"
                className="tw-w-full tw-pl-9 tw-pr-8 tw-py-2 tw-border tw-border-solid tw-border-slate-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
              />
              {term && (
                <button
                  type="button"
                  onClick={() => setTerm('')}
                  title="Clear search"
                  className="tw-absolute tw-right-1.5 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-sm tw-border-0 tw-bg-transparent tw-text-slate-400 hover:tw-text-slate-600 hover:tw-bg-slate-100 tw-cursor-pointer"
                >
                  <LuX size={14} />
                </button>
              )}
            </div>
          )}

          {sourceOptions.length === 0 ? (
            <p className="tw-text-xs tw-text-slate-400 tw-mb-0 tw-flex-shrink-0">No other role to combine.</p>
          ) : visibleSources.length === 0 ? (
            <p className="tw-text-xs tw-text-slate-400 tw-mb-0 tw-flex-shrink-0">No role matches “{term}”.</p>
          ) : (
            /* flex-1 + min-h-0: nở hết chỗ trống rồi mới cuộn, thay vì kẹp cứng
               một chiều cao và để trống nửa drawer.
               Block chứ không phải flex column: flex item bị co lại cho vừa chiều
               cao thay vì cho cuộn, và hàng sẽ bị cắt chữ. */
            <div className="tw-flex-1 tw-min-h-0 tw-space-y-1 tw-overflow-y-auto tw-pr-1 tw-overscroll-contain">
              {visibleSources.map(role => {
                const checked = sourceIds.includes(role.id);
                // Khi đang lọc, role đã tick vẫn hiện dù không khớp từ khoá — tô
                // nền để thấy ngay vì sao nó còn ở đây.
                return (
                  <label
                    key={role.id}
                    className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded tw-border tw-cursor-pointer ${
                      checked
                        ? 'tw-border-blue-200 tw-bg-blue-50'
                        : 'tw-border-slate-200 hover:tw-bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSource(role.id)}
                      disabled={!targetId}
                      className="tw-cursor-pointer tw-flex-shrink-0"
                    />
                    <span className="tw-truncate tw-text-slate-700">{role.name}</span>
                    <span className="tw-ml-auto tw-flex-shrink-0 tw-text-[11px] tw-text-slate-400">ID#{role.id}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Hệ quả — số thật lấy từ backend, không đoán ở client */}
        {sourceIds.length > 0 && (
          <div className="tw-flex-shrink-0 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3">
            <p className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-text-amber-800 tw-mb-2">
              <LuAlertTriangle size={14} /> This cannot be undone
            </p>
            {loading ? (
              <LoadingSpinner size="sm" />
            ) : preview ? (
              <ul className="tw-text-xs tw-text-amber-900 tw-mb-0 tw-pl-4 tw-space-y-1">
                <li>
                  <strong>{preview.added_permissions}</strong> permission(s) added to{' '}
                  <strong>{targetRole?.name || 'the target role'}</strong>
                </li>
                <li>
                  <strong>{preview.moved_users}</strong> user(s) moved onto it — including users of other teams
                </li>
                <li>
                  <strong>{(preview.source_roles || []).length}</strong> role(s) deleted:{' '}
                  {(preview.source_roles || []).map(r => `${r.name} (${r.user_count} user)`).join(', ')}
                </li>
              </ul>
            ) : null}
          </div>
        )}
      </div>
    </Drawer>
  );
};

CombineRolesDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  // Roles the drawer can pick from: the user's roles, or every role in the system.
  roles: PropTypes.arrayOf(PropTypes.object).isRequired,
  // Set on Role detail, where the role being viewed is always the one kept.
  lockedTargetId: PropTypes.number,
  onCombined: PropTypes.func,
};

export default CombineRolesDrawer;
